import { HtmlTypography } from 'src/components/HtmlTypography';
import { Iframely } from 'src/components/Iframely';

import { extractUrlsFromHtml, stripUserCard } from './utils';

interface ActivityContentProps {
  content: string;
}

export const ActivityContent = ({ content }: ActivityContentProps) => {
  const sanitized = stripUserCard(content);
  const urls = extractUrlsFromHtml(sanitized);

  return (
    <>
      <HtmlTypography
        linkifyOptions={{ nl2br: false }}
        sx={(theme) => ({
          position: 'relative',
          width: '100%',
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& .workout-notes .note': {
            backgroundColor: 'info.selected',
            '& p': {
              padding: 1,
            },
          },
          '& .exercise-notes .note': {
            '& p': {
              fontStyle: 'italic',
            },
          },
          '& .activity-mod-stat-list': {
            display: 'flex',
            gap: 4,
            p: 0,
            m: 0,
            listStyle: 'none',
            '& li': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '1.125rem',
              fontWeight: 500,
              '&:empty': {
                display: 'none',
              },
              '& span': {
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              },
            },
          },
          '& .workout-summary .activity-mod-stat-list': {
            '& li': {
              flexDirection: 'row',
            },
          },
          '& .workout-summary.right': {
            float: 'right',
            marginLeft: 3,
            marginBottom: 2,
          },
          '& .exercise-list': {
            clear: 'both',
            listStyle: 'none',
            p: 0,
            mt: 2,
            '& li:empty': {
              display: 'none',
            },
            '& .activity-workout-exercise': {
              position: 'relative',
              mb: 1,
              '& > a:first-of-type': {
                fontWeight: 500,
              },
              '& .show-stats': {
                display: 'none',
              },
            },
          },
          '& .workout-summary.inline': {
            display: 'none',
          },
          '& .user-info': {
            fontSize: '0.875rem',
            '& .user-link': {
              fontWeight: 500,
            },
          },
          '& .photo-container-stream': {
            width: 'fit-content',
            '& .nub': {
              display: 'none',
            },
            '& > div:last-child': {
              background: 'none !important',
              p: 0,
              m: 0,
              '& p': {
                m: 0,
                color: 'text.primary',
                fontSize: '0.875rem',
              },
            },
            '& img': {
              borderRadius: 1,
              maxWidth: '600px !important',
              height: 'auto !important',
              p: '0 !important',
            },
          },
          // Constrain legacy images embedded directly in HTML content
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            display: 'block',
            margin: '8px 0',
            width: 'auto !important',
          },
          // Specific styling for cdn.exercise.com images (legacy images)
          '& img[src*="cdn.exercise.com"]': {
            maxWidth: '600px',
            maxHeight: '500px',
            objectFit: 'contain',
            [theme.breakpoints.down('md')]: {
              maxWidth: '100%',
              maxHeight: '',
            },
          },
          // Clear float after content
          '&::after': {
            content: '""',
            display: 'table',
            clear: 'both',
          },
          '& ul': {
            'li.pr': {
              listStyleImage: `url(https://cdn.exercise.com/assets/layout/star-list.png)`,
            },
            'li.sr': {
              listStyleImage: `url(https://cdn.exercise.com/assets/workout-tracker/star-list-orange.png)`,
            },
            'li.start-superset': {
              padding: 1,
              borderTop: '2px solid',
              borderTopColor: 'primary.main',
            },
          },
          '& .superset-label': {
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '2px 8px',
            backgroundColor: 'primary.main',
            color: 'white',
          },
        })}
      >
        {sanitized}
      </HtmlTypography>
      {urls.map((u) => (
        <Iframely key={u} url={u} />
      ))}
    </>
  );
};
